import './accentEssay.css';
import { useState } from "react";
import { Say } from "../widgets/Say";
import { loadEnglishDictionaries } from "../../loader/loadEnglishDictionaries";
import { DictionaryLookup } from "./DictionaryLookup";
import { LexicalSets } from "./LexicalSets";
import { EnglishAccentType } from "../../ruleSets/accentRules";
import { Say2 } from '../widgets/Say2';
import { accentColors, vowelColor, yorkshireColor, join } from './constants';
import { SayAccentPhones } from './SayAccent';
import { MultiLookup } from './MultiLookup';
import { CustomSets } from '../rules/CustomSets';
import { HelpCustomSets } from '../rules/HelpCustomSets';
import { PSlash } from '../widgets/Delimit';

/** accents to show in table of lexical sets */
const accentsForTable: EnglishAccentType[] = ['English RP', 'Western US', 'Scouse (Liverpool)', 'Glasgow', 'Australian'];
/** accents to show in dictionary lookup list */
const accentsForLookup: EnglishAccentType[] = ['Western US', 'English RP', 'Cockney', 'Scouse (Liverpool)',
  'New Dublin', 'Local Dublin', 'Scottish', 'Glasgow', 'Welsh (Cardiff)', 'Australian', 'Shakespeare'];

/** essay describing how to make various English accents */
export function AccentEssay() {
  const [init, setInit] = useState(false);
  const [loaded, setLoaded] = useState(false);
  if (!init) {
    setInit(true);
    loadEnglishDictionaries().then(() => {
      setLoaded(true);
    });
  }

  return (<div className='page'>
    <h1>Making English Accents</h1>

    <p>
      This essay discusses how to predict the pronunciation of English words in different accents.
      It uses two pronunciation dictionaries as input: one for American English and one for British English.
      It then applies knowledge of various accents to turn those general pronunciations into accent-specific pronunciations.
      You can hear the end result spoken by a speech synthesizer:
      click to hear text spoken when you see <Say>a triangle followed by underlined text</Say>.
    </p>

    <p>
      <b>Caveats:</b> The results should only be considered an approximation.
      The accuracy is limited by what's in the pronouncing dictionaries and the sound change rules that get applied,
      which will no doubt sound more accurate for some words and accents than others.
      While the speech synthesizer supports a wide range of sounds, it doesn't support every sound needed by all English accents.
      Note also that this is only about <i>pronunciation</i>, not vocabulary or idioms.
      An actual speaker may use completely different words to convey the same concepts.
    </p>

    <p>
      <b>Symbols:</b> The pronunciations are described using IPA,
      the <a href="https://en.wikipedia.org/wiki/International_Phonetic_Alphabet">International Phonetic Alphabet</a>.
      To get a feel for IPA, type in any English word below to see how the US/American and UK/British dictionaries represent it.
      (Note that there will be quirkiness in the words the dictionaries contain, some of the pronunciations, and
      where the stress markers are put - the UK dictionary puts stress before the vowel rather than the syllable, etc.)
    </p>

    {loaded ? <DictionaryLookup /> : <div>loading dictionaries...</div>}

    <p>
      Topics in this essay include <a href="#sets">lexical sets</a>, <a href="#r-sounds">r sounds</a>,&nbsp;
      <a href="#other-changes">other sound changes</a>, <a href="#lengths">lengths of sounds</a>,&nbsp;
      <a href="#by-accent">pronunciations by accent</a>, and <a href="#design">design your own</a>.
    </p>

    <h2 id="sets">Lexical sets</h2>

    <p>
      A <i>lexical set</i> is a set of words that share certain sound traits.
      English lexical sets are sets of English words where the vowel sounds tend to change together across accents.
      And since vowels give an accent a lot of its personality, that's a good place to start when defining accents.
    </p>

    <p>
      For example, GOAT is the representative word for a lexical set that includes <i>soap</i>, <i>joke</i>, <i>know</i>, and <i>roll</i>.
      If an accent uses <SayV>əʊ̯</SayV> in <SayRP>goat</SayRP>, it will probably use the same sound across all the other words:
      &nbsp;<SayRP>soap</SayRP>, <SayRP>joke</SayRP>, <SayRP>know</SayRP>, and <SayRP>roll</SayRP>.
      If a different accent uses <SayV>o</SayV> in <SayGl>goat</SayGl>, then the other words will probably be
      pronounced <SayGl>soap</SayGl>, <SayGl>joke</SayGl>, <SayGl>know</SayGl>, and <SayGl>roll</SayGl>.
    </p>

    <p>
      The following table lists the standard lexical sets and what they sound like in a few accents, with a few notes:
    </p>

    <ul>
      <li>Lexical sets were originally defined relative to two reference accents, English Recieved Pronunciation (RP) and
        General American (GA), so two of the columns show the canonical vowels in these reference accents,
        though the actual pronunciation might vary a bit from this.</li>
      <li>The rows show the representative word for a lexical set and how it's pronounced in a few accents.</li>
      <li>The columns show a single accent, where each table cell shows the vowel and pronunciation of the representative word.</li>
      <li>Note that some words have additional, accent-specific rules applied, for example
        <Say2>t</Say2> might change to <Say2>θ</Say2> or <Say2>f</Say2> in specific cases.
        Or <Say2>ə</Say2> might change to <Say2>ɐ</Say2> or <Say2>ɨ</Say2> depending on context.</li>
      <li>Symbols in parentheses, such as in <PSlash>ə(ɹ)</PSlash>, depend on context and will be explained in the <i>R sounds</i> section below.</li>
    </ul>

    <LexicalSets accents={accentsForTable} accentColors={accentColors} vowelColor={vowelColor} includeRefAccents={true} />

    <p>
      So how can we figure out which lexical set or sets a given word is in?
      If you look down the RP and GA columns in the table above, you'll see that some of the vowels are unique across sets.
      For example, <SayV>ɪ</SayV> uniqely identifies the KIT lexical set.
      Some have a unique combination of vowels between RP and GA.
      For example, RP: <SayV>ɒ</SayV> and GA: <SayV>ɑ</SayV> is unique to LOT,
      while RP: <SayV>ɒ</SayV> and GA: <SayV>ɔ</SayV> is unique to CLOTH.
      That means we can compare the pronunciations we find in our UK and US
      dictionaries (corresponding to RP and GA) to identify LOT and CLOTH.
    </p>

    <p>
      But if you look closely, you'll find one pair of sets we can't differentiate from our two dictionaries: NORTH and FORCE.
      These sets have merged in both RP and GA, while they have remained distinct in some accents.
      For example, a Local Dublin accent would say <SayLD>north</SayLD> and <SayLD>force</SayLD>.
      In order to differentiate NORTH and FORCE, we need to rely on lists of words that have
      been identified as being in one or the other set:
      for example <i>nor</i>, <i>assort</i>, <i>orbit</i>, and <i>warm</i> in NORTH
      versus <i>ore</i>, <i>export</i>, <i>board</i>, and <i>oral</i> in FORCE.
    </p>

    <p>
      <b>Caveats:</b> Note that this list of lexical sets doesn't explain all words and accents.
      Some sets are further subdivided for particular accents.
      In Yorkshire, for example, you may hear
      date: <Say textColor={yorkshireColor}>/ˈdɛːt/</Say> and day: <Say textColor={yorkshireColor}>/ˈde̞ɪ̯/</Say>,
      even though both words are part of FACE.
      And lexical sets aren't sufficient to explain more extreme differences in pronunciation
      such as <i>schedule</i> - RP: <SayRP>schedule</SayRP> versus GA: <SayGA>schedule</SayGA> -
      or <i>oregano</i> - RP: <SayRP>oregano</SayRP> versus GA: <SayGA>oregano</SayGA>.
    </p>

    <p>[
      Don't think of lexical sets as a way to <i>explain</i> accents.
      Think of them as a convenient workaround for a lack of historical information on pronunciations
      that would have given us a better way to understand how sounds have changed over the centuries.
      They're useful for <i>guessing</i> how a word might be pronounced in a particular accent, but can easily be wrong.
      ]</p>

    <p>References:</p>
    <ul>
      <li>The standard English lexical sets were introduced by John C. Wells in <i>Accents of English</i>, 1982.</li>
      <li>See <a href="https://en.wikipedia.org/wiki/Lexical_set">lexical sets</a> for a general introduction.</li>
      <li>See <a href="https://en.wikipedia.org/wiki/International_Phonetic_Alphabet_chart_for_English_dialects">lexical sets for English dialects</a> for
        an extensive chart of lexical sets across a wide variety of accents.</li>
      <li>See <a href="https://www.yorku.ca/earmstro/courses/phonetics/lexical_sets.pdf">lexical sets lists</a> for
        lists of words that have been categorized into various lexical sets.</li>
    </ul>


    <h2 id="r-sounds">R sounds</h2>

    <p>
      R-sounds vary in interesting ways across English accents.
      You may hear the r in some accents - <SayWU>hear</SayWU> - which disappears in others - <SayAu>hear</SayAu> - while
      other accents may sometimes turn r into a tap - <SaySc>story</SaySc>.
      Accents that always pronounce r's are called <i>rhotic</i>, while accents that drop r's in some cases are <i>non-rhotic</i>.
    </p>

    <p>
      In the lexical set table above, the non-rhotic accents are indicated by marking the r optional,
      such as ɑː(ɹ) listed for START for an English RP accent.
      For these lexical set sounds, which consist of a vowel followed by an r,
      the r is pronounced when followed by a vowel - safari: <SayRP>safari</SayRP> - but
      not when followed by a consonant - start: <SayRP>start</SayRP> - or
      the end of the word - star: <SayRP>star</SayRP>.
    </p>

    <p>
      Those cases are sufficient for our purposes, though there are a few more details you can read about
      in <a href="https://en.wikipedia.org/wiki/Rhoticity_in_English">Rhoticity in English</a>.
    </p>

    <h2 id="other-changes">Other sound changes</h2>

    <p>
      There are more types of sound changes than just lexical sets and r's.
      Other sounds that often change include <Say2>t</Say2>, <Say2>θ</Say2>, <Say2>ð</Say2>, <Say2>ŋ</Say2>, <Say2>h</Say2>, and <Say2>k</Say2>,
      changing in ways that depend on the accent and context.
      See the <a href="?page=westus-essay">Making a Western US Accent</a> essay for examples
      of how to describe sound changes that depend on context.
    </p>

    <p>
      One of the most common and varied is <Say2>t</Say2>.
      Some examples:
    </p>

    <ul>
      <li>Sometimes it turns into a flap between vowels:
        for example, butter in General American, <SayWU>butter</SayWU>, and Scouse, <SaySc>butter</SaySc>.
        (Note that we use <Say2>ᴅ</Say2> and <Say2>ɾ</Say2> for subtly different flaps).</li>
      <li>Sometimes it turns into a glottal stop between vowels:
        for example, button in General American, <SayWU>button</SayWU>, and Glaswegian, <SayGl>button</SayGl>.</li>
      <li>Sometimes it turns into an h:
        for example, Scouse lot: <SaySc>lot</SaySc> or what: <SaySc>what</SaySc>,
        or sometimes in Local Dublin such as butter: <SayLD>butter</SayLD>.</li>
    </ul>

    <p>
      <Say2>θ</Say2> and <Say2>ð</Say2> often change in similar ways.
      Some examples:
    </p>

    <ul>
      <li>In Glascow, you may hear the <Say2>θ</Say2> in <i>three</i> change to <Say2>f</Say2> - <SayGl>three</SayGl> - and
        the <Say2>ð</Say2> in <i>bother</i> change to <Say2>v</Say2> - <SayGl>bother</SayGl></li>
      <li>In Scouse, you may hear the <Say2>θ</Say2> in <i>thin</i> change to a dental <Say2>t̪</Say2> - <SaySc>thin</SaySc> - and
        the <Say2>ð</Say2> in <i>this</i> change to a dental <Say2>d̪</Say2> - <SaySc>this</SaySc></li>
    </ul>

    <p>
      Some examples of how <Say2>ŋ</Say2> may change:
    </p>

    <ul>
      <li>It may change to <Say2>n</Say2> at the end of a word, for example, Cockney bringing: <SayCo>bringing</SayCo>.</li>
      <li>An extra sound may get added to the end:
        for example, Scouse can add a g as in along: <SaySc>along</SaySc> and
        Cardiff can add a k as in thing: <SayAccentPhones accent='Welsh (Cardiff)'>thing</SayAccentPhones>.</li>
    </ul>

    <p>
      Some examples of how <Say2>h</Say2> may change:
    </p>

    <ul>
      <li>It may change to <Say2>ʔ</Say2> at the start of a word, for example, Cockney how: <SayCo>how</SayCo>.</li>
      <li>It may disappear at the start of a word, for example, Scouse how: <SaySc>how</SaySc>.</li>
    </ul>

    <p>
      An example of how <Say2>k</Say2> may change:
    </p>

    <ul>
      <li>It changes to <Say2>x</Say2> in Scouse, for example poker: <SaySc>poker</SaySc>.</li>
    </ul>


    <h2 id="lengths">Lengths of sounds</h2>

    <p>
      While generally not included in a word's pronunciation,
      another detail to consider when trying to accurately pronounce words is how long each sound should last.
      Syllable stress, consonant clusters, and the overall sentence cadence impact sound lengths.
      Since English accents generally vary vowel lengths much more than consonant lengths, we'll focus on vowels.
    </p>

    <p>
      A vowel tends to be shorter when...
    </p>

    <ul>
      <li>in an unstressed syllable</li>
      <li>it's a vowel such as <Say say="/ə/">ə</Say>, <Say say="/ɛ/">ɛ</Say>, <Say say="/ɪ/">ɪ</Say>, or <Say say="/i/">i</Say></li>
      <li>it's a monophthong, a single vowel sound</li>
      <li>it's before a "voiceless" sound like p, t, or f: for example <Say say="[ˈmɛə̯180t]">mat</Say></li>
    </ul>

    <p>
      A vowel tends to be longer when...
    </p>

    <ul>
      <li>in a stressed syllable</li>
      <li>it's a vowel such as <Say say="/ɑ/">ɑ</Say> or <Say say="/o/">o</Say></li>
      <li>when it's a diphthong, blending from one vowel sound to another such as <Say say="/ɔɪ̯/">ɔɪ̯</Say>,
        or triphthong, such as <Say say="/æɪ̯ə̯/">æɪ̯ə̯</Say> - this includes
        vowels that travel from a vowel to an r-sound, such as <Say say="/ɪɹ/">ɪɹ</Say>
      </li>
      <li>it's before a "voiced" sound like b, d, or v: for example <Say say="[ˈmɛə̯270d]">mad</Say></li>
      <li>it's at the end of a syllable: for example <Say say="[ˈsi7]">sea</Say> versus <Say say="[ˈsi6d]">sead</Say> versus <Say say="[ˈsi4t]">seat</Say></li>
    </ul>

    <p>
      To give you an idea of lengths, for a General American accent, the settings for the speech synthesizer
      range from 285 ms (milliseconds) for the <Say>/ɔɪ̯/</Say> in <Say>choice</Say>, to
      250 ms for the <Say>/oɹ/</Say> in <Say>north</Say>, to 230 ms for the <Say>/ɛɪ̯/</Say> in <Say>face</Say>, to
      200 ms for the <Say>/ɑ/</Say> in <Say>lot</Say>, to 170 ms for the <Say>/i/</Say> in <Say>fleece</Say>, to
      140 ms for the <Say>/ə/</Say> in <Say>strut</Say>, to 110 ms at the end of <Say>focus</Say>, to
      merging with the n at the end of <Say>button</Say>.
    </p>

    <p>
      When the same sound can be spoken with two different lengths in different words,
      the longer version has an extra symbol added on that looks like a colon,
      for example <Say say="/e/">e</Say> versus <Say say="/eː/">eː</Say>.
      In some Scottish accents, for instance, the <i>e</i> in <i>face</i> is longer
      than the <i>e</i> in <i>kit</i>, so you would write <Say>/ˈfeːs/</Say> versus <Say>/ˈket/</Say>.
    </p>


    <h2 id="by-accent">Pronunciations by accent</h2>

    <p>
      Type in a word and press <i>enter</i> or the <i>lookup</i> button
      to see its pronunciation in the US and UK dictionaries, which lexical sets it finds,
      and how it's pronounced in a few different English accents.
      You can then click on a pronunciation to hear it spoken.
      Or click on the accent name to learn more about it.
      (Many caveats: it's blindly applying rules that are incomplete and not sufficient for full accuracy.
      Don't make any financial decisions based on these results.)
    </p>

    <MultiLookup accents={accentsForLookup} />


    <h2 id="design">Design your own</h2>

    <p>
      Would you like to try your hand at creating your own custom English accent?
      The interface below lets you assign vowels to the lexical sets we previously discussed,
      starting from a variety of English accents.
      You can pick monophthongs (one vowel), diphthongs (two vowels), triphthongs (three vowels),
      nasals, and rhotics (various types of r-sounds), and even customize the vowel lengths.
      At any time, you can type in sample text and hear it spoken in your new accent.
    </p>

    <HelpCustomSets />
    <hr />
    <CustomSets />
    <hr />

    <br /><br />
  </div>);
}


/** pronounce a vowel or diphthong, properly colored, avoiding linebreaks, wrapped in // */
function SayV(props: { children: string }) {
  const text = `/${join}${props.children}${join}/`;
  return <Say textColor={vowelColor} say={`/${props.children}/`}>{text}</Say>;
}

function SayGA(props: { children: string }) {
  return <SayAccentPhones accent='Dictionary US' vowelColor={accentColors['Western US']}>{props.children}</SayAccentPhones>
}

function SayWU(props: { children: string }) {
  return <SayAccentPhones accent='Western US'>{props.children}</SayAccentPhones>
}

function SayRP(props: { children: string }) {
  return <SayAccentPhones accent='English RP'>{props.children}</SayAccentPhones>
}

function SayCo(props: { children: string }) {
  return <SayAccentPhones accent='Cockney'>{props.children}</SayAccentPhones>
}

function SaySc(props: { children: string }) {
  return <SayAccentPhones accent='Scouse (Liverpool)'>{props.children}</SayAccentPhones>
}

function SayGl(props: { children: string }) {
  return <SayAccentPhones accent='Glasgow'>{props.children}</SayAccentPhones>
}

function SayAu(props: { children: string }) {
  return <SayAccentPhones accent='Australian'>{props.children}</SayAccentPhones>
}

function SayLD(props: { children: string }) {
  return <SayAccentPhones accent='Local Dublin'>{props.children}</SayAccentPhones>
}
